import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert
} from '@mui/material';
import { useTlbHoursOptions } from '../components/hooks/useTlbHoursOptions';

/**
 * Per-schedule TLB hours / Hobbs counter (e.g. HOBBS METER AC for AC compressor AMP tasks).
 * @param {{ aircraftId: string, currentUsageTotals: object|null, value: string, onChange: (key: string) => void, disabled?: boolean, dialogOpen?: boolean, size?: 'small'|'medium' }} props
 */
export default function ScheduleTlbHoursCounterField({
  aircraftId,
  currentUsageTotals = null,
  value = '',
  onChange,
  disabled = false,
  dialogOpen = true,
  size = 'small'
}) {
  const { tlbHoursOptions, tlbHoursOptionsLoading } = useTlbHoursOptions({
    open: dialogOpen && !!aircraftId,
    aircraftId,
    currentUsageTotals
  });

  if (!aircraftId) {
    return (
      <Alert severity="info" sx={{ py: 0.5 }}>
        Select an aircraft first to choose which TLB hours / Hobbs counter this schedule uses.
      </Alert>
    );
  }

  const selected = value ? tlbHoursOptions.find((o) => o.key === value) : null;

  return (
    <Box>
      <FormControl fullWidth size={size} disabled={disabled || tlbHoursOptionsLoading}>
        <InputLabel id="schedule-tlb-hours-label">TLB hours counter</InputLabel>
        <Select
          labelId="schedule-tlb-hours-label"
          label="TLB hours counter"
          displayEmpty
          value={value || ''}
          onChange={(e) => onChange(e.target.value || '')}
          renderValue={(selectedKey) => {
            if (!selectedKey) {
              return (
                <Typography component="span" variant="body2" color="text.secondary">
                  {tlbHoursOptionsLoading ? 'Loading counters…' : 'Select counter (optional)'}
                </Typography>
              );
            }
            const opt = tlbHoursOptions.find((o) => o.key === selectedKey);
            if (!opt) return selectedKey;
            return `${opt.label} — ${opt.liveDisplay}`;
          }}
        >
          <MenuItem value="">
            <em>Use tenant default (Live TLB mapping → Hours / Airframe)</em>
          </MenuItem>
          {tlbHoursOptions.map((opt) => (
            <MenuItem key={opt.key} value={opt.key}>
              {opt.label} — {opt.liveDisplay}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {selected && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          TLB live (carried): {selected.liveDisplay}
        </Typography>
      )}
      {!value && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Leave unset to use Airframe Hours. Choose HOBBS METER AC for air-conditioning compressor inspections.
        </Typography>
      )}
    </Box>
  );
}
